<script setup lang="ts">
import { computed, watch } from "vue";
import crossIcon from "#ui/assets/icons/cross.svg";
import { type DecorScreen, nullDecor } from "#ui/decoration/screen";
import DecorIcon from "#ui/primitives/decorIcon/DecorIcon.vue";
import KsoCup from "#ui/templates/kso/ksoCup/KsoCup.vue";
import KsoWaves from "#ui/templates/kso/ksoWaves/KsoWaves.vue";
import { useRotatingPhrases } from "../../../hooks/useRotatingPhrases/useRotatingPhrases";
import { useCreepProgress } from "./useCreepProgress";

export interface Props {
	immersive?: boolean;
	active?: boolean;
	completed?: boolean;
	phrases?: string[];
	phraseInterval?: number | null;
	caption?: string;
	hint?: string;
	cupIcon: string;
	cupDefaultIcon?: boolean;
	decor?: DecorScreen;
}

const props = withDefaults(defineProps<Props>(), {
	immersive: true,
	active: true,
	completed: false,
	phrases: () => [],
	phraseInterval: null,
	caption: "",
	hint: "",
	cupDefaultIcon: false,
	decor: () => nullDecor,
});

const emit = defineEmits<{
	cancel: [];
}>();

const isImmersive = computed(() => props.decor.variant("kso_loader", props.immersive ? "immersive" : "simple") === "immersive");
const closeIcon = computed(() => props.decor.icon("kso_loader_close"));

const { phrase, index } = useRotatingPhrases(
	() => props.phrases,
	() => props.phraseInterval,
	() => props.active && isImmersive.value,
);

const { progress, complete } = useCreepProgress(() => props.active);

const progressStyle = computed(() => ({ transform: `scaleX(${Math.max(progress.value, 0.02)})` }));

watch(
	() => props.completed,
	(isCompleted) => {
		if (isCompleted) complete();
	},
	{ immediate: true },
);
</script>

<template>
	<div
		class="kso_loader tw:flex tw:flex-col tw:w-full tw:min-h-(--kso-loader-height,380px) tw:min-[1023px]:min-h-(--kso-loader-height,520px)"
		:data-mode="isImmersive ? 'immersive' : 'simple'"
		v-bind="props.decor.bind('kso_loader')"
	>
		<KsoWaves v-if="props.decor.visible('kso_waves', isImmersive)" :decor="props.decor" />
		<div
			class="kso_loader_body tw:relative tw:bg-(--accentColor) tw:text-[#fff] tw:flex tw:flex-1 tw:flex-col tw:items-center tw:justify-center tw:gap-18 tw:p-[10px_24px_40px] tw:text-center tw:[[data-mode=simple]>&]:rounded-[var(--kso-panel-radius,24px)_var(--kso-panel-radius,24px)_0_0] tw:[[data-mode=simple]>&]:gap-16 tw:[[data-mode=simple]>&]:p-[40px_24px] tw:min-[1023px]:gap-24 tw:min-[1023px]:p-[16px_40px_56px] tw:min-[1023px]:[[data-mode=simple]>&]:p-[56px_40px]"
			v-bind="props.decor.bind('kso_loader_body')"
		>
			<button
				type="button"
				class="kso_creating_close tw:absolute tw:top-16 tw:right-16 tw:z-2 tw:min-w-44 tw:min-h-44 tw:flex tw:items-center tw:justify-center tw:p-0 tw:[border:none] tw:[background:none] tw:[outline:none] tw:[-webkit-tap-highlight-color:transparent] tw:[transition:transform_0.12s_ease] tw:active:[transform:scale(0.92)] tw:[&_#custom\_svg\_lib]:w-45 tw:[&_#custom\_svg\_lib]:h-45 tw:[&_#custom\_svg\_lib]:[fill:var(--accentColor)] tw:min-[1023px]:top-24 tw:min-[1023px]:right-24 tw:min-[1023px]:[&_#custom\_svg\_lib]:w-60 tw:min-[1023px]:[&_#custom\_svg\_lib]:h-60"
				v-bind="props.decor.bind('kso_loader_close')"
				@click="emit('cancel')"
			>
				<DecorIcon v-if="closeIcon" :icon="closeIcon" :fallback="crossIcon" />
				<slot v-else name="close-icon" />
			</button>
			<template v-if="isImmersive">
				<KsoCup :icon="props.cupIcon" :default-icon="props.cupDefaultIcon" :decor="props.decor" />

				<div
					class="kso_loader_phrase_box tw:h-[3.75rem] tw:flex tw:items-center tw:justify-center tw:overflow-hidden tw:min-[1023px]:h-[5.25rem]"
					v-bind="props.decor.bind('kso_loader_phrase_box')"
				>
					<Transition
						name="kso_phrase"
						mode="out-in"
						enter-active-class="kso_phrase-enter-active tw:[transition:opacity_0.3s_ease,transform_0.3s_ease]"
						leave-active-class="kso_phrase-leave-active tw:[transition:opacity_0.3s_ease,transform_0.3s_ease]"
						enter-from-class="kso_phrase-enter-from tw:opacity-0 tw:[transform:translateY(8px)]"
						leave-to-class="kso_phrase-leave-to tw:opacity-0 tw:[transform:translateY(-8px)]"
					>
						<p
							:key="index"
							class="kso_loader_phrase tw:[font-size:calc(1.5rem*var(--fontFit-x,1))] tw:line-clamp-2 tw:text-ellipsis tw:[font-family:var(--fontMain)] tw:[font-weight:normal] tw:[line-height:calc(1.25/var(--fontFit-x,1))] tw:m-0 tw:max-w-[22ch] tw:min-[1023px]:[font-size:calc(2.1rem*var(--fontFit-x,1))]"
							v-bind="props.decor.bind('kso_loader_phrase')"
						>
							{{ phrase }}
						</p>
					</Transition>
				</div>

				<div class="kso_loader_dots tw:flex tw:items-center tw:gap-8" aria-hidden="true" v-bind="props.decor.bind('kso_loader_dots')">
					<i
						class="tw:w-8 tw:aspect-square tw:rounded-[50%] tw:bg-[rgba(255,255,255,0.4)] tw:animate-[kso\_dot_1.4s_ease-in-out_infinite] tw:nth-2:[animation-delay:0.2s] tw:nth-3:[animation-delay:0.4s] tw:motion-reduce:animate-none tw:min-[1023px]:w-11"
						v-bind="props.decor.bind('kso_loader_dot')"
					/>
					<i
						class="tw:w-8 tw:aspect-square tw:rounded-[50%] tw:bg-[rgba(255,255,255,0.4)] tw:animate-[kso\_dot_1.4s_ease-in-out_infinite] tw:nth-2:[animation-delay:0.2s] tw:nth-3:[animation-delay:0.4s] tw:motion-reduce:animate-none tw:min-[1023px]:w-11"
						v-bind="props.decor.bind('kso_loader_dot')"
					/>
					<i
						class="tw:w-8 tw:aspect-square tw:rounded-[50%] tw:bg-[rgba(255,255,255,0.4)] tw:animate-[kso\_dot_1.4s_ease-in-out_infinite] tw:nth-2:[animation-delay:0.2s] tw:nth-3:[animation-delay:0.4s] tw:motion-reduce:animate-none tw:min-[1023px]:w-11"
						v-bind="props.decor.bind('kso_loader_dot')"
					/>
				</div>
			</template>

			<p
				v-else
				class="kso_loader_title tw:[font-size:calc(1.5rem*var(--fontFit-x,1))] tw:line-clamp-2 tw:text-ellipsis tw:[font-family:var(--fontMain)] tw:[font-weight:normal] tw:[line-height:calc(1.25/var(--fontFit-x,1))] tw:m-0 tw:max-w-[22ch] tw:min-[1023px]:[font-size:calc(2.1rem*var(--fontFit-x,1))]"
				v-bind="props.decor.bind('kso_loader_title')"
			>
				{{ props.caption }}
			</p>

			<div
				class="kso_loader_progress tw:relative tw:w-[min(100%,420px)] tw:h-6 tw:rounded-[4px] tw:bg-[rgba(255,255,255,0.16)] tw:overflow-hidden tw:min-[1023px]:h-9"
				role="progressbar"
				v-bind="props.decor.bind('kso_loader_progress')"
			>
				<span
					class="kso_loader_progress_fill tw:absolute tw:inset-0 tw:rounded-[4px] tw:bg-[#fff] tw:origin-[left_center] tw:will-change-transform tw:[transition:transform_0.2s_linear]"
					:style="progressStyle"
					v-bind="props.decor.bind('kso_loader_fill')"
				/>
				<span
					v-if="props.decor.visible('kso_loader_glare')"
					class="kso_loader_progress_glare tw:absolute tw:top-0 tw:bottom-0 tw:left-0 tw:w-[40%] tw:bg-[linear-gradient(90deg,transparent,rgba(255,255,255,0.65),transparent)] tw:animate-[kso\_glare_1.8s_ease-in-out_infinite] tw:motion-reduce:animate-none"
					v-bind="props.decor.bind('kso_loader_glare')"
				/>
			</div>

			<p
				class="kso_loader_caption tw:m-0 tw:[font-size:calc(1rem*var(--fontFit-x,1))] tw:text-[rgba(255,255,255,0.62)] tw:min-[1023px]:[font-size:calc(1.4rem*var(--fontFit-x,1))]"
				v-bind="props.decor.bind('kso_loader_caption')"
			>
				{{ isImmersive ? props.caption : props.hint }}
			</p>
		</div>
	</div>
</template>

<style lang="scss">
  @use './ksoOrderLoader.scss';
</style>
